import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface GovernanceStandards {
  readonly saasEnabled: boolean;
  readonly tenantVerificationRequired: boolean;
  readonly tenantAutoProvision: boolean;
  readonly defaultIsolationModel: string;
  readonly usageAlertThresholdPct: number;
  readonly mfaEnforcementDefault: boolean;
  readonly encryptionAtRestDefault: boolean;
  readonly encryptionInTransitDefault: boolean;
  readonly passwordMinLength: number;
  readonly sessionTimeoutMinutes: number;
  readonly backupRetentionDays: number;
  readonly analyticsRetentionDays: number;
  readonly auditRetentionYears: number;
  readonly domainVerificationTtlHours: number;
  readonly sslExpiryAlertDays: number;
}

const ROW = (label: string, key: keyof GovernanceStandards, value: string | number | boolean): ReactNode => (
  <tr key={key}>
    <th scope="row">{label}</th>
    <td aria-label={`standard-${key}`}>{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}</td>
  </tr>
);

/**
 * M43 §27 Governance Dashboard (§20 Platform Governance). Surfaces the enforced
 * governance standards for the operator — tenant policies, security standards,
 * configuration standards, retention / release / compliance windows — resolved
 * from the LAW-6 parameters via `/v1/saas/governance/standards`. Viewing needs
 * TENANT_VIEW.
 */
export function GovernanceDashboard(): ReactNode {
  const { token } = useAuth();
  const [data, setData] = useState<GovernanceStandards | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: GovernanceStandards }>('/v1/saas/governance/standards', { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="governance-dashboard">
      <h1>Governance Dashboard</h1>
      {error && <p role="alert" aria-label="governance-error">{error}</p>}
      {loading ? <p>Loading…</p> : data && (
        <>
          <section aria-label="tenant-standards">
            <h2>Tenant Standards</h2>
            <table aria-label="tenant-standards-table">
              <tbody>
                {ROW('SaaS enabled', 'saasEnabled', data.saasEnabled)}
                {ROW('Verification required', 'tenantVerificationRequired', data.tenantVerificationRequired)}
                {ROW('Auto-provision', 'tenantAutoProvision', data.tenantAutoProvision)}
                {ROW('Default isolation', 'defaultIsolationModel', data.defaultIsolationModel)}
                {ROW('Usage alert threshold %', 'usageAlertThresholdPct', data.usageAlertThresholdPct)}
              </tbody>
            </table>
          </section>

          <section aria-label="security-standards">
            <h2>Security Standards</h2>
            <table aria-label="security-standards-table">
              <tbody>
                {ROW('MFA enforced', 'mfaEnforcementDefault', data.mfaEnforcementDefault)}
                {ROW('Encryption at rest', 'encryptionAtRestDefault', data.encryptionAtRestDefault)}
                {ROW('Encryption in transit', 'encryptionInTransitDefault', data.encryptionInTransitDefault)}
                {ROW('Password min length', 'passwordMinLength', data.passwordMinLength)}
                {ROW('Session timeout (min)', 'sessionTimeoutMinutes', data.sessionTimeoutMinutes)}
              </tbody>
            </table>
          </section>

          <section aria-label="retention-standards">
            <h2>Retention &amp; Compliance Standards</h2>
            <table aria-label="retention-standards-table">
              <tbody>
                {ROW('Backup retention (days)', 'backupRetentionDays', data.backupRetentionDays)}
                {ROW('Analytics retention (days)', 'analyticsRetentionDays', data.analyticsRetentionDays)}
                {ROW('Audit retention (years)', 'auditRetentionYears', data.auditRetentionYears)}
                {ROW('Domain verification TTL (h)', 'domainVerificationTtlHours', data.domainVerificationTtlHours)}
                {ROW('SSL expiry alert (days)', 'sslExpiryAlertDays', data.sslExpiryAlertDays)}
              </tbody>
            </table>
          </section>
        </>
      )}
    </main>
  );
}
